import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, getAuthHeaders, handleApiError } from '../../App';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Card } from '../../components/ui/card';
import { Plus, Calendar, Lock, IndianRupee, Save } from 'lucide-react';
import { toast } from 'sonner';
import SalaryReportTab from '../../components/staff/SalaryReportTab';
import EmployeeProfileDialog from '../../components/staff/EmployeeProfileDialog';
import AddDeductionDialog from '../../components/staff/AddDeductionDialog';
import MonthlyAttendanceGrid from '../../components/staff/MonthlyAttendanceGrid';

const ATTENDANCE_PASSWORD = process.env.REACT_APP_ATTENDANCE_PASSWORD || '';
const BUSINESS_NAME = 'Shri Kripa Traders';

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const StaffAttendance = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const [staff, setStaff] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [payroll, setPayroll] = useState(null);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [showAddStaff, setShowAddStaff] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [profileStaff, setProfileStaff] = useState(null);
  const [deductionStaff, setDeductionStaff] = useState(null);
  const [newDeduction, setNewDeduction] = useState({ amount: '', reason: 'Advance', date: new Date().toISOString().split('T')[0] });

  // Navigation
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [viewMode, setViewMode] = useState('monthly'); // monthly | salary
  const [pendingCells, setPendingCells] = useState({}); // optimistic UI map

  const [newStaff, setNewStaff] = useState({ name: '', phone: '', role: '', monthly_salary: '', joining_date: '' });

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, selectedMonth, viewMode]);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (password === ATTENDANCE_PASSWORD) {
      setIsAuthenticated(true);
      setPasswordError('');
    } else {
      setPasswordError('Incorrect password');
    }
  };

  const fetchData = async () => {
    try {
      const calls = [
        axios.get(`${API}/staff`, { headers: getAuthHeaders() }),
        axios.get(`${API}/attendance?month=${selectedMonth}`, { headers: getAuthHeaders() }),
      ];
      if (viewMode === 'salary') {
        calls.push(axios.get(`${API}/staff/payroll/${selectedMonth}`, { headers: getAuthHeaders() }));
      }
      const results = await Promise.all(calls);
      setStaff(results[0].data);
      setAttendance(results[1].data);
      if (viewMode === 'salary') setPayroll(results[2].data); else setPayroll(null);
      setPendingCells({});
    } catch (error) {
      const message = handleApiError(error, 'Failed to fetch data');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddStaff = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: newStaff.name,
        phone: newStaff.phone,
        role: newStaff.role || '',
        monthly_salary: parseFloat(newStaff.monthly_salary) || 0,
        joining_date: newStaff.joining_date || '',
      };
      await axios.post(`${API}/staff`, payload, { headers: getAuthHeaders() });
      toast.success('Staff added successfully');
      setShowAddStaff(false);
      setNewStaff({ name: '', phone: '', role: '', monthly_salary: '', joining_date: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add staff');
    }
  };

  const handleUpdateStaff = async () => {
    try {
      await axios.put(`${API}/staff/${editingStaff.id}`, {
        name: editingStaff.name,
        phone: editingStaff.phone,
        role: editingStaff.role || '',
        monthly_salary: parseFloat(editingStaff.monthly_salary) || 0,
        joining_date: editingStaff.joining_date || '',
      }, { headers: getAuthHeaders() });
      toast.success('Staff updated');
      setEditingStaff(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update staff');
    }
  };

  const handleDeleteStaff = async (staffId) => {
    if (!window.confirm('Are you sure you want to delete this staff member? All attendance and salary records will also be deleted.')) return;
    try {
      await axios.delete(`${API}/staff/${staffId}`, { headers: getAuthHeaders() });
      toast.success('Staff deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete staff');
    }
  };

  /** Auto-save attendance on cell click. Optimistic UI: update pendingCells
   *  immediately, fire-and-update server, then refresh attendance silently.  */
  const cycleCell = async (staffId, date, currentStatus, nextStatus) => {
    const key = `${staffId}|${date}`;
    setPendingCells((prev) => ({ ...prev, [key]: nextStatus }));
    try {
      if (nextStatus === '') {
        await axios.delete(`${API}/attendance?staff_id=${staffId}&date=${date}`, { headers: getAuthHeaders() });
      } else {
        await axios.post(`${API}/attendance`, { staff_id: staffId, date, status: nextStatus }, { headers: getAuthHeaders() });
      }
      // Update local attendance array without a full refetch for snappy UX
      setAttendance((prev) => {
        const filtered = prev.filter((a) => !(a.staff_id === staffId && a.date === date));
        if (!nextStatus) return filtered;
        return [...filtered, { staff_id: staffId, date, status: nextStatus }];
      });
      setPendingCells((prev) => {
        const cp = { ...prev };
        delete cp[key];
        return cp;
      });
    } catch (err) {
      toast.error(handleApiError(err, 'Failed to save'));
      // Revert on error
      setPendingCells((prev) => {
        const cp = { ...prev };
        delete cp[key];
        return cp;
      });
    }
  };

  const getMonthReport = (staffId) => {
    const staffAttendance = attendance.filter((a) => a.staff_id === staffId);
    return {
      present: staffAttendance.filter((a) => a.status === 'present').length,
      absent: staffAttendance.filter((a) => a.status === 'absent').length,
      half_day: staffAttendance.filter((a) => a.status === 'half_day').length,
    };
  };

  const sendWhatsAppReport = (staffMember) => {
    const report = getMonthReport(staffMember.id);
    const monthName = new Date(selectedMonth + '-01').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    const message = `Hello ${staffMember.name},\n\nYour attendance report for ${monthName}:\n\n✅ Present: ${report.present} days\n❌ Absent: ${report.absent} days\n⏰ Half Day: ${report.half_day} days\n\nTotal Working Days: ${report.present + report.absent + report.half_day}\n\n- ${BUSINESS_NAME}`;
    const phone = (staffMember.phone || '').replace(/[^0-9]/g, '');
    if (!phone) return toast.error('No phone number for this staff');
    window.open(`https://wa.me/91${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const sendWhatsAppSalarySlip = (row) => {
    const monthName = new Date(selectedMonth + '-01').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    const lines = [
      `*Salary Slip - ${monthName}*`,
      `Name: ${row.staff_name}`,
      row.role ? `Role: ${row.role}` : null,
      '',
      `Monthly Salary: ${fmtINR(row.monthly_salary)}`,
      `Daily Wage: ${fmtINR(row.daily_wage)} (Month: ${row.days_in_month} days)`,
      '',
      `Present: ${row.present} days`,
      `Half Day: ${row.half_day} days`,
      `Absent: ${row.absent} days`,
      '',
      `Gross Salary: ${fmtINR(row.gross_salary)}`,
      `Deductions (Advance etc.): ${fmtINR(row.total_deduction)}`,
      `*Net Payable: ${fmtINR(row.net_payable)}*`,
      '',
      `- ${BUSINESS_NAME}`,
    ].filter(Boolean).join('\n');
    const phone = (row.phone || '').replace(/[^0-9]/g, '');
    if (!phone) return toast.error('No phone number for this staff');
    window.open(`https://wa.me/91${phone}?text=${encodeURIComponent(lines)}`, '_blank');
  };

  const sendWhatsAppFullPayrollSummary = () => {
    if (!payroll || !payroll.staff?.length) return toast.info('No payroll data');
    const monthName = new Date(selectedMonth + '-01').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    const header = `*Payroll Summary - ${monthName}*\n`;
    const rows = payroll.staff.map((r, i) =>
      `${i + 1}. ${r.staff_name}: Gross ${fmtINR(r.gross_salary)} - Ded ${fmtINR(r.total_deduction)} = *${fmtINR(r.net_payable)}*`
    ).join('\n');
    const totals = `\n\nTotal Gross: ${fmtINR(payroll.totals.gross)}\nTotal Deductions: ${fmtINR(payroll.totals.deductions)}\n*Total Net Payable: ${fmtINR(payroll.totals.net)}*`;
    const message = header + '\n' + rows + totals + `\n\n- ${BUSINESS_NAME}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  const addDeduction = async (e) => {
    e.preventDefault();
    if (!deductionStaff) return;
    try {
      await axios.post(`${API}/staff/adjustment`, {
        staff_id: deductionStaff.staff_id || deductionStaff.id,
        month: selectedMonth,
        amount: parseFloat(newDeduction.amount) || 0,
        reason: newDeduction.reason || 'Advance',
        date: newDeduction.date,
      }, { headers: getAuthHeaders() });
      toast.success('Deduction added');
      setDeductionStaff(null);
      setNewDeduction({ amount: '', reason: 'Advance', date: new Date().toISOString().split('T')[0] });
      fetchData();
    } catch (error) {
      toast.error('Failed to add deduction');
    }
  };

  const deleteDeduction = async (adjId) => {
    if (!window.confirm('Remove this deduction?')) return;
    try {
      await axios.delete(`${API}/staff/adjustment/${adjId}`, { headers: getAuthHeaders() });
      toast.success('Deduction removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove deduction');
    }
  };

  const printSalaryReport = () => {
    if (!payroll) return;
    const monthName = new Date(selectedMonth + '-01').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    const rowsHtml = payroll.staff.map((r, i) => `
      <tr>
        <td>${i + 1}</td>
        <td>${r.staff_name}</td>
        <td>${r.role || '-'}</td>
        <td class="r">${fmtINR(r.monthly_salary)}</td>
        <td class="c">${r.present}</td>
        <td class="c">${r.half_day}</td>
        <td class="c">${r.absent}</td>
        <td class="r">${fmtINR(r.gross_salary)}</td>
        <td class="r">${fmtINR(r.total_deduction)}</td>
        <td class="r bold">${fmtINR(r.net_payable)}</td>
      </tr>
    `).join('');
    const html = `<!doctype html><html><head><title>Salary Report - ${monthName}</title>
    <style>
      body{font-family:Arial, sans-serif;padding:24px;color:#111}
      h1{margin:0;font-size:22px}
      h2{margin:4px 0 16px;font-size:14px;color:#555;font-weight:500}
      table{width:100%;border-collapse:collapse;font-size:12px}
      th,td{border:1px solid #d4d4d4;padding:6px 8px}
      th{background:#f3f4f6;text-align:left}
      td.r{text-align:right} td.c{text-align:center} td.bold{font-weight:700}
      tfoot td{background:#f9fafb;font-weight:700}
      @media print{body{padding:10px}}
    </style></head><body>
    <h1>${BUSINESS_NAME} - Salary Report</h1>
    <h2>${monthName} (${payroll.days_in_month} days)</h2>
    <table>
      <thead><tr>
        <th>#</th><th>Staff</th><th>Role</th><th>Monthly Sal.</th>
        <th>P</th><th>H</th><th>A</th><th>Gross</th><th>Ded.</th><th>Net</th>
      </tr></thead>
      <tbody>${rowsHtml}</tbody>
      <tfoot><tr>
        <td colspan="7" class="r">Totals</td>
        <td class="r">${fmtINR(payroll.totals.gross)}</td>
        <td class="r">${fmtINR(payroll.totals.deductions)}</td>
        <td class="r">${fmtINR(payroll.totals.net)}</td>
      </tr></tfoot>
    </table>
    <p style="margin-top:20px;font-size:11px;color:#666">Printed on ${new Date().toLocaleString('en-IN')}</p>
    </body></html>`;
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, '_blank');
    if (w) {
      w.onload = () => { w.print(); setTimeout(() => URL.revokeObjectURL(url), 1000); };
    }
  };

  // Print monthly attendance (full grid) — landscape A4
  const printMonthlyAttendance = () => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const dim = new Date(y, m, 0).getDate();
    const monthName = new Date(selectedMonth + '-01').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    const dayHeaders = Array.from({ length: dim }, (_, i) => i + 1)
      .map((d) => {
        const dt = new Date(`${selectedMonth}-${String(d).padStart(2, '0')}`);
        const wd = dt.toLocaleDateString('en-IN', { weekday: 'short' }).slice(0, 1);
        const we = dt.getDay() === 0;
        return `<th class="${we ? 'wkn' : ''}"><div class="wd">${wd}</div><div>${d}</div></th>`;
      }).join('');
    const rowsHtml = staff.map((s, i) => {
      let p = 0, a = 0, h = 0;
      const cells = Array.from({ length: dim }, (_, idx) => {
        const date = `${selectedMonth}-${String(idx + 1).padStart(2, '0')}`;
        const rec = attendance.find((x) => x.staff_id === s.id && x.date === date);
        const st = rec ? rec.status : '';
        if (st === 'present') p++;
        else if (st === 'absent') a++;
        else if (st === 'half_day') h++;
        const lbl = st === 'present' ? 'P' : st === 'absent' ? 'A' : st === 'half_day' ? 'H' : '';
        const cls = st === 'present' ? 'p' : st === 'absent' ? 'a' : st === 'half_day' ? 'hd' : '';
        return `<td class="${cls}">${lbl}</td>`;
      }).join('');
      return `<tr><td class="idx">${i + 1}</td><td class="nm">${s.name}${s.role ? `<div class="role">${s.role}</div>` : ''}</td>${cells}<td class="sumc">${p}</td><td class="sumc">${h}</td><td class="sumc">${a}</td></tr>`;
    }).join('');
    const html = `<!doctype html><html><head><title>Monthly Attendance - ${monthName}</title>
    <style>
      @page{size:A4 landscape;margin:8mm}
      *{box-sizing:border-box}
      body{font-family:Arial,sans-serif;color:#111;font-size:9px;padding:0;margin:0}
      h1{margin:0;font-size:16px}
      h2{margin:2px 0 10px;font-size:11px;color:#555;font-weight:500}
      table{width:100%;border-collapse:collapse;table-layout:fixed}
      th,td{border:1px solid #c0c0c0;padding:2px;text-align:center}
      th{background:#1e293b;color:#fff;font-size:8px}
      th.wkn{background:#7f1d1d}
      td.idx{width:24px;color:#666}
      td.nm{text-align:left;font-weight:600;width:130px;padding:2px 4px}
      td.nm .role{font-size:7px;color:#666;font-weight:400}
      td.p{background:#16a34a;color:#fff;font-weight:700}
      td.a{background:#dc2626;color:#fff;font-weight:700}
      td.hd{background:#d97706;color:#fff;font-weight:700}
      td.sumc{background:#f1f5f9;font-weight:700;width:30px}
      .wd{font-size:7px;opacity:0.7;line-height:1}
    </style></head><body>
    <h1>${BUSINESS_NAME} — Monthly Attendance</h1>
    <h2>${monthName} · ${dim} days · ${staff.length} staff</h2>
    <table>
      <thead><tr><th style="width:24px">#</th><th style="width:130px;text-align:left;padding-left:4px">Staff</th>${dayHeaders}<th style="background:#16a34a">P</th><th style="background:#d97706">H</th><th style="background:#dc2626">A</th></tr></thead>
      <tbody>${rowsHtml}</tbody>
    </table>
    <p style="margin-top:10px;font-size:8px;color:#666">Printed ${new Date().toLocaleString('en-IN')} · Legend: P=Present, A=Absent, H=Half Day</p>
    </body></html>`;
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, '_blank');
    if (w) {
      w.onload = () => { w.print(); setTimeout(() => URL.revokeObjectURL(url), 1000); };
    }
  };

  // Generate per-employee monthly ledger (Print → Save as PDF)
  // Compact single-page A4 layout: calendar attendance + side-by-side info + summary
  const generateEmployeeLedgerPDF = async (row) => {
    try {
      const [year, mon] = selectedMonth.split('-').map(Number);
      const daysInMonth = new Date(year, mon, 0).getDate();
      const firstDow = new Date(year, mon - 1, 1).getDay(); // 0=Sun..6=Sat

      // Fetch this staff's attendance + adjustments for the month
      const [attRes, adjRes] = await Promise.all([
        axios.get(`${API}/attendance?month=${selectedMonth}&staff_id=${row.staff_id}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/staff/adjustments?month=${selectedMonth}&staff_id=${row.staff_id}`, { headers: getAuthHeaders() }),
      ]);
      const attendanceMap = {};
      attRes.data.forEach(a => { attendanceMap[a.date] = a.status; });
      const advances = adjRes.data || [];

      const monthName = new Date(selectedMonth + '-01').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

      // Build calendar cells: leading empty pads + day cells
      const absentDates = [];
      const halfDates = [];
      const cells = [];
      for (let i = 0; i < firstDow; i++) cells.push('<div class="cal-cell empty"></div>');
      for (let d = 1; d <= daysInMonth; d++) {
        const dateStr = `${selectedMonth}-${String(d).padStart(2, '0')}`;
        const status = attendanceMap[dateStr] || '';
        let cls = 'st-none';
        let label = '';
        if (status === 'present') { cls = 'st-p'; label = 'P'; }
        else if (status === 'half_day') { cls = 'st-h'; label = 'H'; halfDates.push(dateStr); }
        else if (status === 'absent') { cls = 'st-a'; label = 'A'; absentDates.push(dateStr); }
        cells.push(`<div class="cal-cell"><div class="d">${d}</div><div class="s ${cls}">${label || '—'}</div></div>`);
      }
      // pad trailing to fill row of 7
      while (cells.length % 7 !== 0) cells.push('<div class="cal-cell empty"></div>');
      const calendarHtml = cells.join('');

      const formatDate = (s) => new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
      const advanceRows = advances.length
        ? advances.map((a, i) => `<tr><td class="c">${i + 1}</td><td>${formatDate(a.date)}</td><td>${a.reason || 'Advance'}</td><td class="r">${fmtINR(a.amount)}</td></tr>`).join('')
        : '<tr><td colspan="4" class="c muted">No advances</td></tr>';

      const totalAdvance = advances.reduce((s, a) => s + Number(a.amount || 0), 0);
      const monthlySalary = Number(row.monthly_salary || 0);
      const dailyWage = daysInMonth ? (monthlySalary / daysInMonth) : 0;
      const grossSalary = +(dailyWage * row.present + 0.5 * dailyWage * row.half_day).toFixed(2);
      const netPayable = +(grossSalary - totalAdvance).toFixed(2);

      const dateChips = (arr) => arr.length
        ? arr.map(d => `<span class="chip">${formatDate(d)}</span>`).join('')
        : '<span class="muted">—</span>';

      const html = `<!doctype html><html><head><meta charset="utf-8" />
<title>Salary Ledger - ${row.staff_name} - ${monthName}</title>
<style>
  @page { size: A4 portrait; margin: 8mm; }
  *{box-sizing:border-box;margin:0;padding:0}
  html,body{font-family:'Helvetica',Arial,sans-serif;color:#0f172a;font-size:9px;line-height:1.3}
  body{padding:0}
  h2{font-size:10px;color:#1e293b;border-left:3px solid #0ea5e9;padding:1px 0 1px 6px;margin:6px 0 3px}
  .brand{display:flex;align-items:center;gap:8px;border-bottom:1.5px solid #0f172a;padding-bottom:5px;margin-bottom:6px}
  .logo{width:30px;height:30px;border-radius:50%;background:linear-gradient(135deg,#1e3a8a,#0ea5e9);color:#fff;font-weight:800;font-size:13px;display:flex;align-items:center;justify-content:center;flex-shrink:0}
  .brand .title h1{font-size:14px;letter-spacing:.4px;line-height:1.1}
  .brand .title .sub{font-size:8px;color:#475569;letter-spacing:.8px;text-transform:uppercase;margin-top:1px}
  .brand .right{margin-left:auto;text-align:right;font-size:8px;color:#475569}
  .brand .right b{font-size:11px;color:#0f172a;display:block}

  /* Single row of 8 meta cells */
  .meta{display:grid;grid-template-columns:repeat(8,1fr);gap:4px;margin-bottom:6px}
  .cell{background:#f8fafc;border:1px solid #e2e8f0;border-radius:4px;padding:4px 5px;min-width:0}
  .cell .label{font-size:7px;color:#64748b;text-transform:uppercase;letter-spacing:.4px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .cell .value{font-weight:700;color:#0f172a;font-size:10px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}

  /* Calendar attendance */
  .cal{display:grid;grid-template-columns:repeat(7,1fr);gap:2px}
  .cal-head{display:grid;grid-template-columns:repeat(7,1fr);gap:2px;margin-bottom:2px}
  .cal-head div{background:#1e293b;color:#fff;text-align:center;font-size:8px;padding:2px 0;border-radius:3px;font-weight:600}
  .cal-cell{border:1px solid #cbd5e1;border-radius:3px;padding:2px;min-height:24px;display:flex;flex-direction:column;align-items:center;justify-content:center}
  .cal-cell.empty{background:repeating-linear-gradient(45deg,#f1f5f9,#f1f5f9 3px,#fff 3px,#fff 6px);border-color:#e2e8f0}
  .cal-cell .d{font-size:9px;font-weight:700;color:#64748b;line-height:1}
  .cal-cell .s{font-size:8px;font-weight:700;border-radius:3px;padding:0 4px;margin-top:2px;line-height:1.4;min-width:14px;text-align:center}
  .st-p{background:#16a34a;color:#fff}
  .st-h{background:#d97706;color:#fff}
  .st-a{background:#dc2626;color:#fff}
  .st-none{background:#f1f5f9;color:#cbd5e1}
  .legend{display:flex;gap:8px;font-size:8px;color:#475569;justify-content:flex-end;margin-top:2px}
  .legend span{display:inline-flex;align-items:center;gap:3px}
  .legend i{display:inline-block;width:8px;height:8px;border-radius:2px}

  /* Three-up info cards */
  .grid-3{display:grid;grid-template-columns:1fr 1fr 1.4fr;gap:6px;margin-top:6px}
  .card{border:1px solid #cbd5e1;border-radius:4px;padding:4px 6px;min-height:50px}
  .card .ttl{font-size:8px;color:#475569;text-transform:uppercase;letter-spacing:.5px;margin-bottom:3px;font-weight:700}
  .card.red .ttl{color:#dc2626}
  .card.amber .ttl{color:#d97706}
  .card.blue .ttl{color:#0ea5e9}
  .chips{display:flex;flex-wrap:wrap;gap:2px}
  .chip{background:#f1f5f9;border:1px solid #e2e8f0;border-radius:3px;padding:1px 4px;font-size:8px}
  .muted{color:#94a3b8;font-size:8px}

  table{width:100%;border-collapse:collapse;font-size:8px}
  th,td{border:1px solid #cbd5e1;padding:2px 4px}
  th{background:#1e293b;color:#fff;font-weight:600;text-align:left;font-size:8px}
  td.c{text-align:center} td.r{text-align:right}
  tfoot td{font-weight:700;background:#f8fafc}

  /* Salary summary — compact 2-col */
  .summary{display:grid;grid-template-columns:1fr 1fr;gap:2px 14px;background:#f1f5f9;border:1px solid #cbd5e1;border-radius:4px;padding:6px 8px;margin-top:6px;font-size:9px}
  .summary .ln{display:flex;justify-content:space-between;padding:1px 0}
  .summary .net{grid-column:1 / -1;display:flex;justify-content:space-between;border-top:1.5px solid #0f172a;margin-top:3px;padding-top:4px;font-size:11px;font-weight:800;color:#059669}

  .footer{margin-top:6px;display:flex;justify-content:space-between;font-size:7px;color:#64748b;border-top:1px solid #e2e8f0;padding-top:3px}
</style></head>
<body>

<div class="brand">
  <div class="logo">SK</div>
  <div class="title">
    <h1>${BUSINESS_NAME}</h1>
    <div class="sub">Salary Ledger · ${monthName}</div>
  </div>
  <div class="right">
    <b>${row.staff_name}</b>
    ${row.role || ''} ${row.phone ? '· ' + row.phone : ''}
  </div>
</div>

<div class="meta">
  <div class="cell"><div class="label">Employee</div><div class="value">${row.staff_name}</div></div>
  <div class="cell"><div class="label">Role</div><div class="value">${row.role || '—'}</div></div>
  <div class="cell"><div class="label">Phone</div><div class="value">${row.phone || '—'}</div></div>
  <div class="cell"><div class="label">Monthly Sal.</div><div class="value">${fmtINR(monthlySalary)}</div></div>
  <div class="cell"><div class="label">Days</div><div class="value">${daysInMonth}</div></div>
  <div class="cell"><div class="label">Present</div><div class="value" style="color:#16a34a">${row.present}</div></div>
  <div class="cell"><div class="label">Half</div><div class="value" style="color:#d97706">${row.half_day}</div></div>
  <div class="cell"><div class="label">Absent</div><div class="value" style="color:#dc2626">${row.absent}</div></div>
</div>

<h2>Daily Attendance — ${monthName}</h2>
<div class="cal-head">
  <div>SUN</div><div>MON</div><div>TUE</div><div>WED</div><div>THU</div><div>FRI</div><div>SAT</div>
</div>
<div class="cal">${calendarHtml}</div>
<div class="legend">
  <span><i style="background:#16a34a"></i>Present</span>
  <span><i style="background:#d97706"></i>Half Day</span>
  <span><i style="background:#dc2626"></i>Absent</span>
  <span><i style="background:#f1f5f9;border:1px solid #cbd5e1"></i>Not Marked</span>
</div>

<div class="grid-3">
  <div class="card red">
    <div class="ttl">Absent Dates (${absentDates.length})</div>
    <div class="chips">${dateChips(absentDates)}</div>
  </div>
  <div class="card amber">
    <div class="ttl">Half-Day Dates (${halfDates.length})</div>
    <div class="chips">${dateChips(halfDates)}</div>
  </div>
  <div class="card blue">
    <div class="ttl">Advance History (${advances.length}) · Total ${fmtINR(totalAdvance)}</div>
    <table>
      <thead><tr><th style="width:14px">#</th><th style="width:60px">Date</th><th>Reason</th><th style="width:60px;text-align:right">Amt</th></tr></thead>
      <tbody>${advanceRows}</tbody>
    </table>
  </div>
</div>

<div class="summary">
  <div class="ln"><span>Monthly Salary</span><span>${fmtINR(monthlySalary)}</span></div>
  <div class="ln"><span>Days in Month</span><span>${daysInMonth}</span></div>
  <div class="ln"><span>Daily Wage (Sal ÷ Days)</span><span>${fmtINR(dailyWage)}</span></div>
  <div class="ln"><span>Present (${row.present}) × Daily</span><span>${fmtINR(dailyWage * row.present)}</span></div>
  <div class="ln"><span>Half (${row.half_day}) × ½ Daily</span><span>${fmtINR(0.5 * dailyWage * row.half_day)}</span></div>
  <div class="ln"><span>Absent (${row.absent})</span><span>− ${fmtINR(0)}</span></div>
  <div class="ln" style="font-weight:700;border-top:1px dashed #94a3b8;padding-top:3px;margin-top:2px"><span>Gross Salary</span><span>${fmtINR(grossSalary)}</span></div>
  <div class="ln" style="font-weight:700;border-top:1px dashed #94a3b8;padding-top:3px;margin-top:2px"><span>Total Advance</span><span style="color:#dc2626">− ${fmtINR(totalAdvance)}</span></div>
  <div class="net"><span>Net Payable</span><span>${fmtINR(netPayable)}</span></div>
</div>

<div class="footer">
  <div>Generated: ${new Date().toLocaleString('en-IN')}</div>
  <div>${BUSINESS_NAME} · Confidential · Single-page Ledger</div>
</div>

</body></html>`;

      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      if (w) {
        w.onload = () => { w.print(); setTimeout(() => URL.revokeObjectURL(url), 1000); };
      }
    } catch (err) {
      toast.error('Failed to generate ledger PDF');
    }
  };


  const changeMonth = (delta) => {
    const date = new Date(selectedMonth + '-01');
    date.setMonth(date.getMonth() + delta);
    setSelectedMonth(date.toISOString().slice(0, 7));
  };

  // Password protection
  if (!isAuthenticated) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Card className="w-full max-w-md p-8 border-slate-200">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="h-8 w-8 text-blue-600" />
              </div>
              <h1 className="text-2xl font-heading tracking-tight text-slate-900 mb-2">Staff Attendance</h1>
              <p className="text-slate-500">Enter password to access this module</p>
            </div>
            <form onSubmit={handlePasswordSubmit} className="space-y-4" data-testid="attendance-password-form">
              <div className="space-y-2">
                <Label>Password</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="h-12"
                  data-testid="attendance-password-input"
                />
                {passwordError && <p className="text-sm text-red-600">{passwordError}</p>}
              </div>
              <Button type="submit" className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white" data-testid="attendance-unlock-btn">
                Unlock
              </Button>
            </form>
          </Card>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-slate-500">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="staff-attendance-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-heading tracking-tight text-slate-900 mb-2">Staff Attendance</h1>
            <p className="text-slate-500">Attendance, payroll & monthly salary reports</p>
          </div>
          <div className="flex gap-2">
            <Dialog open={showAddStaff} onOpenChange={setShowAddStaff}>
              <DialogTrigger asChild>
                <Button className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-staff-btn">
                  <Plus className="h-4 w-4 mr-2" />Add Staff
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-heading tracking-tight">Add New Staff</DialogTitle>
                  <DialogDescription>Set employee details, role and monthly salary.</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleAddStaff} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2 col-span-2">
                      <Label>Name *</Label>
                      <Input value={newStaff.name} onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })} required className="h-11" data-testid="add-staff-name" />
                    </div>
                    <div className="space-y-2">
                      <Label>Mobile *</Label>
                      <Input value={newStaff.phone} onChange={(e) => setNewStaff({ ...newStaff, phone: e.target.value })} placeholder="9876543210" required className="h-11" data-testid="add-staff-phone" />
                    </div>
                    <div className="space-y-2">
                      <Label>Role</Label>
                      <Input value={newStaff.role} onChange={(e) => setNewStaff({ ...newStaff, role: e.target.value })} placeholder="e.g. Salesman" className="h-11" data-testid="add-staff-role" />
                    </div>
                    <div className="space-y-2">
                      <Label>Monthly Salary (₹)</Label>
                      <Input type="number" min="0" value={newStaff.monthly_salary} onChange={(e) => setNewStaff({ ...newStaff, monthly_salary: e.target.value })} placeholder="0" className="h-11" data-testid="add-staff-salary" />
                    </div>
                    <div className="space-y-2">
                      <Label>Joining Date</Label>
                      <Input type="date" value={newStaff.joining_date} onChange={(e) => setNewStaff({ ...newStaff, joining_date: e.target.value })} className="h-11" data-testid="add-staff-joining" />
                    </div>
                  </div>
                  <Button type="submit" className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-staff-submit">
                    Add Staff
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* View Mode Toggle */}
        <div className="flex gap-2 flex-wrap">
          <Button
            variant={viewMode === 'monthly' ? 'default' : 'outline'}
            onClick={() => setViewMode('monthly')}
            className={viewMode === 'monthly' ? 'bg-blue-600 text-white hover:bg-blue-700' : ''}
            data-testid="tab-monthly"
          >
            <Calendar className="h-4 w-4 mr-2" />Monthly Attendance
          </Button>
          <Button
            variant={viewMode === 'salary' ? 'default' : 'outline'}
            onClick={() => setViewMode('salary')}
            className={viewMode === 'salary' ? 'bg-emerald-600 text-white hover:bg-emerald-700' : ''}
            data-testid="tab-salary"
          >
            <IndianRupee className="h-4 w-4 mr-2" />Salary Report
          </Button>
        </div>

        {/* Monthly Attendance View */}
        {viewMode === 'monthly' && (
          <MonthlyAttendanceGrid
            staff={staff}
            attendance={attendance}
            selectedMonth={selectedMonth}
            setSelectedMonth={setSelectedMonth}
            onChangeMonth={changeMonth}
            onCycleCell={cycleCell}
            onPrint={printMonthlyAttendance}
            onEditStaff={setEditingStaff}
            onDeleteStaff={handleDeleteStaff}
            onProfileClick={setProfileStaff}
            pendingCells={pendingCells}
          />
        )}

        {/* Salary Report View */}
        {viewMode === 'salary' && (
          <SalaryReportTab
            selectedMonth={selectedMonth}
            setSelectedMonth={setSelectedMonth}
            payroll={payroll}
            onChangeMonth={changeMonth}
            onPrint={printSalaryReport}
            onWhatsappSummary={sendWhatsAppFullPayrollSummary}
            onStaffClick={(r) => setProfileStaff({ id: r.staff_id, name: r.staff_name, phone: r.phone, role: r.role, monthly_salary: r.monthly_salary })}
            onDeductClick={(r) => setDeductionStaff(r)}
            onWhatsappSlip={sendWhatsAppSalarySlip}
            onDownloadPDF={generateEmployeeLedgerPDF}
          />
        )}

        {/* Edit Staff Dialog */}
        <Dialog open={!!editingStaff} onOpenChange={() => setEditingStaff(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-heading tracking-tight">Edit Staff</DialogTitle>
            </DialogHeader>
            {editingStaff && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2 col-span-2">
                    <Label>Name</Label>
                    <Input value={editingStaff.name} onChange={(e) => setEditingStaff({ ...editingStaff, name: e.target.value })} className="h-10" data-testid="edit-staff-name" />
                  </div>
                  <div className="space-y-2">
                    <Label>Mobile</Label>
                    <Input value={editingStaff.phone} onChange={(e) => setEditingStaff({ ...editingStaff, phone: e.target.value })} className="h-10" data-testid="edit-staff-phone" />
                  </div>
                  <div className="space-y-2">
                    <Label>Role</Label>
                    <Input value={editingStaff.role || ''} onChange={(e) => setEditingStaff({ ...editingStaff, role: e.target.value })} className="h-10" placeholder="e.g. Salesman" data-testid="edit-staff-role" />
                  </div>
                  <div className="space-y-2">
                    <Label>Monthly Salary (₹)</Label>
                    <Input type="number" min="0" value={editingStaff.monthly_salary || ''} onChange={(e) => setEditingStaff({ ...editingStaff, monthly_salary: e.target.value })} className="h-10" data-testid="edit-staff-salary" />
                  </div>
                  <div className="space-y-2">
                    <Label>Joining Date</Label>
                    <Input type="date" value={editingStaff.joining_date || ''} onChange={(e) => setEditingStaff({ ...editingStaff, joining_date: e.target.value })} className="h-10" data-testid="edit-staff-joining" />
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" onClick={handleUpdateStaff} data-testid="edit-staff-save">
                    <Save className="h-4 w-4 mr-1" />Save
                  </Button>
                  <Button variant="outline" className="flex-1" onClick={() => setEditingStaff(null)}>Cancel</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Employee Profile Popup */}
        <EmployeeProfileDialog
          profileStaff={profileStaff}
          selectedMonth={selectedMonth}
          monthReport={profileStaff ? getMonthReport(profileStaff.id) : { present: 0, absent: 0, half_day: 0 }}
          onClose={() => setProfileStaff(null)}
          onEdit={() => {
            if (!profileStaff) return;
            const full = staff.find(s => s.id === profileStaff.id) || profileStaff;
            setProfileStaff(null);
            setEditingStaff(full);
          }}
          onWhatsapp={() => profileStaff && sendWhatsAppReport(profileStaff)}
        />

        {/* Add Deduction Dialog */}
        <AddDeductionDialog
          staff={deductionStaff}
          selectedMonth={selectedMonth}
          newDeduction={newDeduction}
          setNewDeduction={setNewDeduction}
          onSubmit={addDeduction}
          onClose={() => setDeductionStaff(null)}
          onDeleteAdjustment={deleteDeduction}
        />
      </div>
    </Layout>
  );
};

export default StaffAttendance;
